import React from "react";
import styled, { css } from "styled-components";
import * as yup from "yup";
import { toast } from "react-toastify";

import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import {
  ICallOff,
  LIST_CUSTOMER_CALL_OFFS_CACHE_KEY,
  useCallOffApi
} from "src/modules/call-off/api/call-off.api";
import {
  CheckboxStyled,
  FormRow,
  Input,
  Label
} from "src/library/components/Forms";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { ProductMuliSelect } from "src/core/products/components/ProductMultiSelect";
import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import CallOffSideBar from "src/modules/call-off/components/callOffInfoSideBar";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { CustomerMultiSelect } from "src/core/customers/components/CustomerMultiSelect";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Tooltip } from "src/library/components/Tooltip";
import useCache from "src/core/shared/hooks/useCache";

export const callOffSchema = yup.object().shape({
  name: yup.string().required(),
  customerReference: yup.string().required(),
  initialQuantity: yup.number().required(),
  currentQuantity: yup.number().required("Current Quantity is Required").max(yup.ref("initialQuantity"), "Current Quantity Cannot Exceed Initial Quantity"),
  productId: yup.number().required("A Product is Required"),
  customerId: yup.number().required("A Customer is Required"),
  startDate: yup
    .date()
    .label("Start date")
    .required(),
  endDate: yup
    .date().nullable()
    .label("End date"),
  exclusiveOrdering: yup.boolean()
});

const CreateEditOrderCallOffsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [ createCallOffRes, createCallOff ] = useCallOffApi("CREATE_CALL_OFF");
  const [ updateCallOffRes, updateCallOff ] = useCallOffApi("UPDATE_CALL_OFF");
  const [ getCallOffRes, getCallOff ] = useCallOffApi("GET_CALL_OFF");

  const {
    history, match, terminology
  } = props;

  const editCallOffId = match && match.params.callOffId ? parseInt(match.params.callOffId, 10) : null;
  const [ form, setForm ] = React.useState<Partial<ICallOff|null>>({ exclusiveOrdering: false });
  const [ forceExclusiveOrdering, setForceExclusiveOrdering ] = React.useState<boolean>(false);
  const { removeItemByKey } = useCache();

  React.useEffect(() => {
    if (editCallOffId) {
      getCallOff({ id: editCallOffId });
    }
  }, [ editCallOffId, getCallOff ]);

  React.useEffect(() => {
    setForm(getCallOffRes?.data);
  }, [ getCallOffRes.data ]);

  const [ validation, setValidation ] = React.useState<{ error: boolean; message: string }>({
    error: false,
    message: ""
  });

  const handleFormChange = React.useCallback(
    (name: keyof ICallOff, value: any) => {
      setValidation({
        error: false,
        message: ""
      });

      setForm(currentForm => ({
        ...currentForm,
        [ name ]: value
      }));
    }, [ ]
  );

  React.useEffect(() => {
    if (createCallOffRes.data) {
      toast.success("Successfully Created Call-Off");
      history.push("/admin/orders/call-offs");
      removeItemByKey(`${createCallOffRes.data?.customerId}_${LIST_CUSTOMER_CALL_OFFS_CACHE_KEY}`);
    } else if (createCallOffRes.error) {
      toast.error(createCallOffRes.error.message);
    }
  }, [
    createCallOffRes.data,
    createCallOffRes.error,
    history,
    removeItemByKey
  ]);

  React.useEffect(() => {
    if (updateCallOffRes.data) {
      toast.success("Successfully Updated Call-Off");
      history.push("/admin/orders/call-offs");
      removeItemByKey(`${updateCallOffRes.data?.customerId}_${LIST_CUSTOMER_CALL_OFFS_CACHE_KEY}`);
    } else if (updateCallOffRes.error) {
      toast.error(updateCallOffRes.error.message);
    }
  }, [
    history,
    removeItemByKey,
    updateCallOffRes.data,
    updateCallOffRes.error
  ]);

  const saveCallOff = React.useCallback(() => {
    if (form?.id) {
      updateCallOff({
        callOffId: form.id,
        callOffUpdate: form
      });
    } else {
      createCallOff(form as ICallOff);
    }
  }, [
    createCallOff,
    form,
    updateCallOff
  ]);

  const handleSubmit = React.useCallback(() => {
    callOffSchema
      .validate(form)
      .then(() => {
        saveCallOff();
      })
      .catch(err => {
        setValidation({
          error: true,
          message: err.errors
        });
      });
  }, [ form, saveCallOff ]);

  const primaryButton = React.useMemo(() => {
    return editCallOffId ? (
      <div className="submit-form">
        <Button
          loading={updateCallOffRes.pending}
          $buttonType="primary"
          onClick={() => handleSubmit()}
        >
          {`Update ${terminology.callOff?.singular.text ?? "Call off"}`}
        </Button>
      </div>
    ) : (
      <div className="submit-form">
        <Button
          loading={createCallOffRes.pending}
          $buttonType="primary"
          onClick={() => handleSubmit()}
        >
          {`Create ${terminology.callOff?.singular.text ?? "Call off"}`}
        </Button>
      </div>
    );
  }, [
    createCallOffRes.pending,
    editCallOffId,
    handleSubmit,
    terminology.callOff?.singular.text,
    updateCallOffRes.pending
  ]);

  const cancelButton = React.useMemo(() => {
    return (
      <Button
        loading={createCallOffRes.pending}
        $buttonType="primary"
        onClick={() => history.push("/admin/orders/call-offs")}
      >
        Cancel
      </Button>
    );
  }, [ createCallOffRes.pending, history ]);

  return (
    <Layout {...props}>
      <MainContentWrapper>

        {getCallOffRes.pending ? <LoadingSpinner type="circular" /> : (
          <FormContainer>

            {validation.error && (
              <ErrorNotice
                type="warn"
                message={validation.message}
              />
            )}

            <FormRow>
              <Input
                as="input"
                handleChange={e => handleFormChange("name", e.target.value)}
                name="name"
                label="Name"
                inputType="text"
                defaultValue={editCallOffId ? getCallOffRes.data?.name : ""}
                validation={callOffSchema.fields.name}
                value={form?.name}
              />
            </FormRow>

            <FormRow>
              <Input
                as="input"
                handleChange={e => handleFormChange("customerReference", e.target.value)}
                name="customerReference"
                label="Customer Reference"
                inputType="text"
                defaultValue={editCallOffId ? getCallOffRes.data?.customerReference : ""}
                validation={callOffSchema.fields.customerReference}
                value={form?.customerReference}
              />
            </FormRow>

            <FormRow>
              <Input
                as="input"
                handleChange={e => {
                  handleFormChange("initialQuantity", e.target.value);

                  if (!editCallOffId) {
                    handleFormChange("currentQuantity", e.target.value);
                  }
                }}
                name="initialQuantity"
                label="Initial Quantity"
                inputType="number"
                value={form?.initialQuantity}
                defaultValue={editCallOffId ? getCallOffRes.data?.initialQuantity.toString() : "0"}
                validation={callOffSchema.fields.initialQuantity}
              />
            </FormRow>

            <FormRow>
              <div className="selected-dates">
                <div>
                  <Text as="h4">
                    Start Date
                  </Text>

                  <DateInput
                    disabled={editCallOffId ? true : false}
                    label="Start Date"
                    date={form?.startDate ? new Date(form.startDate) : undefined}
                    initialFocusDate={new Date()}
                    setDate={date => handleFormChange("startDate", date)}
                  />
                </div>

                <div>
                  <Text as="h4">
                    End Date (Optional)
                  </Text>

                  <div className="end-date">
                    <DateInput
                      label="End Date"
                      date={form?.endDate ? new Date(form.endDate) : undefined}
                      setDate={date => handleFormChange("endDate", date)}
                    />

                    {form?.endDate && (
                      <Icon
                        icon={Icons.CLOSE}
                        onClick={() => handleFormChange("endDate", null)}
                      />
                    )}

                  </div>
                </div>
              </div>

            </FormRow>

            {!editCallOffId && (
              <FormRow>
                <div className="exclusive-container">
                  <div
                    data-tip
                    data-for="call-off-help"
                    className="label-with-help"
                  >
                    <Label
                      label="Exclusive to Call-Off Ordering"
                      for="call-off-exclusive"
                    />

                    <Icon
                      icon={Icons.HELP}
                      size={10}
                    />
                  </div>

                  <div className="check-box-info">

                    <CheckboxStyled
                      className={`styled-checkbox ${form?.exclusiveOrdering ? "checked" : ""}`}
                      onChange={() => handleFormChange("exclusiveOrdering", !form?.exclusiveOrdering)}
                      name="call-off-exclusive"
                      id="call-off-exclusive"
                      checked={forceExclusiveOrdering ? true : form?.exclusiveOrdering || false}
                      disabled={forceExclusiveOrdering}
                    />

                    {forceExclusiveOrdering && (
                      <Text color="secondary">
                        {`The Product Selected may only be ordered using a ${terminology.callOff?.singular.text ?? "Call off"}`}
                      </Text>
                    )}
                  </div>

                  <Tooltip
                    id="call-off-help"
                    type="dark"
                    place="top"
                    getContent={() => {
                      return (
                        <Text color="white">
                          This customer may only order this product using this call-off
                        </Text>
                      );
                    }}
                  />
                </div>
              </FormRow>

            )}

            <FormRow>

              {!form?.customer && (
                <CustomerMultiSelect
                  singleSelection
                  onCustomersChange={({ fullCustomersSelected }) => {
                    setForm(currentForm => ({
                      ...currentForm,
                      customerId: fullCustomersSelected.map(c => c.id)[ 0 ]
                    }));
                  }}
                  validation={callOffSchema.fields.customerId}
                />
              )}

            </FormRow>

            <FormRow>

              {!form?.product && (
                <ProductMuliSelect
                  singleSelection
                  onProductsChange={({ fullProductsSelected }) => {
                    setForm(currentForm => ({
                      ...currentForm,
                      productId: fullProductsSelected.map(c => c.id)[ 0 ]
                    }));

                    if (fullProductsSelected[ 0 ]?.callOffExclusive) {
                      setForceExclusiveOrdering(true);

                      setForm(currentForm => ({
                        ...currentForm,
                        exclusiveOrdering: true
                      }));
                    } else {
                      setForceExclusiveOrdering(false);

                      setForm(currentForm => ({
                        ...currentForm,
                        exclusiveOrdering: false
                      }));
                    }
                  }}
                  validation={callOffSchema.fields.productId}
                />
              )}
            </FormRow>

          </FormContainer>
        )}
      </MainContentWrapper>

      <InfoBarWrapper>
        <CallOffSideBar
          data={getCallOffRes?.data}
          primaryAction={primaryButton}
          secondaryAction = {cancelButton}
          isLoading={getCallOffRes.pending}
          terminology={terminology}
        />

      </InfoBarWrapper>
    </Layout>
  );
};

const FormContainer = styled.div`${({ theme }) => css`
  display: flex;
  flex-direction: column;
  
  .selected-dates {
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing.padding.md};

    .end-date {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: ${theme.spacing.padding.sm};
    }
  }
  
  .label-with-help {
    display: flex;
    width: fit-content;
    flex-direction: row;
  }

  .check-box-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing.padding.md};
  }
  
  .check-box-info span {
    align-self: center;
  }

  .submit-form{
   padding-top: ${theme.spacing.padding.md};
  }`
}
`;

export default CreateEditOrderCallOffsPage;
